import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useHistory, useParams } from 'react-router-dom';
import { Box, Breadcrumbs, Link, Typography, Paper } from '@mui/material';
import { CustomButton } from '../../../../components/CustomButton';
import { ReactComponent as EditIcon } from '../../../../assets/images/edit.svg';
import { ReactComponent as RedBoxIcon } from '../../../../assets/images/box-red.svg';
import { ReactComponent as GreenBoxIcon } from '../../../../assets/images/green-box.svg';
import { ReactComponent as BinIcon } from '../../../../assets/images/bin.svg';
import { DoubleField } from '../../../support/components/DoubleField';
import { CircularProgressCenter } from '../../../../components/CircularProgressCenter';
import { CustomModal } from '../../../../components/CustomModal';
import { useDispatch, useSelector } from 'react-redux';
import {
  activateTemplate,
  archiveTemplate,
  clearArchiveTemplateErrors,
  deleteTemplate,
  getTemplateById,
  getTimeBounds
} from '../../../../store/mailing/actions';
import { getRouteWithParam, ROUTES } from '../../../../constants/routes';
import {
  getMailingCampaignType,
  getMailingColorStatus,
  getMailingStatus,
  getMailingType,
  PRIORITIES,
} from '../../constants';
import { isNaN } from 'formik';
import { setUsersPermissions } from '../../../../utils/setUsersPermissions';
import { visibleBySectionPermission } from '../../../../utils/visibleBySectionPermission';
import { DISABLED_CONTROL_TOOLTIP, PERMISSIONS } from '../../../../constants/variables';
import { NotFoundItems } from '../../../../components/NotFoundItems';

export const DetailTemplate = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const { template, fetching, timeBounds, archiveError } = useSelector(state => state.mailing);
  const [isSuccessModal, setIsSuccessModal] = useState(false);
  const [isArchiveErrorModal, setIsArchiveErrorModal] = useState(false);
  const { userPermissions, currentUsersPermissions } = useSelector((state) => state.userPermissions);
  const permissions = setUsersPermissions(userPermissions, currentUsersPermissions?.permissions);

  const redirectToTemplates = () => history.push(ROUTES.MAILING);

  const isArchivedStatus = template?.status === 'Архив' || template?.status === 'archive';
  const isCreatedStatus = template?.status === 'Создана' || template?.status === 'created';
  const isAllowedToRetrieveMailingTemplates = visibleBySectionPermission(permissions, PERMISSIONS.MAILING.RetrieveMailingTemplates.value);
  const isAllowedToArchiveMailingTemplates = visibleBySectionPermission(permissions, PERMISSIONS.MAILING.ArchiveMailingTemplates.value);
  const isAllowedToDeleteMailingTemplates = visibleBySectionPermission(permissions, PERMISSIONS.MAILING.DeleteMailingTemplates.value);
  const isAllowedToUpdateMailingTemplates = visibleBySectionPermission(permissions, PERMISSIONS.MAILING.UpdateMailingTemplates.value);

  useEffect(() => {
    dispatch(getTimeBounds());
    dispatch(getTemplateById(id));
  }, // eslint-disable-next-line
    []);

  useEffect(() => {
    if (archiveError) {
      setIsArchiveErrorModal(true);
    }
  }, [archiveError]);

  const handleArchiver = () => {
    template?.status === 'archive' ?
      dispatch(deleteTemplate(id, redirectToTemplates)) :
      dispatch(archiveTemplate(id));

    setIsSuccessModal(false);
  };
  const backToWork = () => {
    dispatch(activateTemplate(id));
  };
  const getTimeBound = () => {
    if (!template) return '';

    const foundTimeBound = (timeBounds || []).find((period) => period.slug === template.time_bound);

    return foundTimeBound ? foundTimeBound.name : template.time_bound;
  };

  if (fetching) {
    return <CircularProgressCenter data-testid="CircularProgressCenter"/>;
  }

  return (
    <>
      <Box id="mailing-detail-template">
        <Breadcrumbs mb="24px" aria-label="breadcrumb">
          <Link component={RouterLink} underline="hover" color="inherit" to={ROUTES.MAILING}>
            CMC/Email рассылка
          </Link>

          <Typography sx={{ lineBreak: 'anywhere' }}>{template?.name}</Typography>
        </Breadcrumbs>

        {
          isAllowedToRetrieveMailingTemplates ?
            (
              <>
                <Paper sx={{ padding: '32px' }}>
                  <Box display="flex" mb="16px" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="h1" mb="8px" sx={{ color: 'text.primary', lineBreak: 'anywhere' }} component="h1">
                        {template?.name}
                      </Typography>
                      <Box>{getMailingCampaignType(template?.campaign_type)}</Box>
                    </Box>
                    <Box data-testid="CurrentStatus" color={`${getMailingColorStatus(template?.status)}`}>{getMailingStatus(template?.status)}</Box>
                  </Box>

                  {
                    isArchivedStatus ?
                      (
                        <CustomButton
                          color={'success'}
                          variant="outlined"
                          size="small"
                          sx={{ marginRight: '16px' }}
                          startIcon={<GreenBoxIcon />}
                          onClick={() => isAllowedToArchiveMailingTemplates && backToWork()}
                          disabled={!isAllowedToArchiveMailingTemplates}
                          tooltip={!isAllowedToArchiveMailingTemplates ? DISABLED_CONTROL_TOOLTIP : null}
                        >
                          Вернуть в работу
                        </CustomButton>
                      ) : (
                        isCreatedStatus && !isAllowedToUpdateMailingTemplates ? (
                          <CustomButton
                            color={'secondary'}
                            variant="outlined"
                            size="small"
                            sx={{ marginRight: '16px' }}
                            startIcon={<EditIcon />}
                            disabled={true}
                            tooltip={DISABLED_CONTROL_TOOLTIP}
                          >
                            Редактировать
                          </CustomButton>
                        ) : (
                          <CustomButton
                            color={'secondary'}
                            variant="outlined"
                            size="small"
                            sx={{ marginRight: '16px' }}
                            startIcon={<EditIcon />}
                            component={RouterLink}
                            to={getRouteWithParam(ROUTES.MAILING_ADD_TEMPLATE,':id', id)}
                          >
                            Редактировать
                          </CustomButton>
                        )
                      )
                  }

                {
                  isArchivedStatus ? (
                    isAllowedToDeleteMailingTemplates ? (
                      <CustomButton
                        color="error"
                        variant="outlined"
                        size="small"
                        sx={{ marginRight: '16px' }}
                        startIcon={<BinIcon />}
                        onClick={() => setIsSuccessModal(true)}
                      >
                        Удалить
                      </CustomButton>
                    ) : (
                      <CustomButton
                        color="error"
                        variant="outlined"
                        size="small"
                        sx={{ marginRight: '16px' }}
                        startIcon={<BinIcon />}
                        disabled={true}
                        tooltip={DISABLED_CONTROL_TOOLTIP}
                      >
                        Удалить
                      </CustomButton>
                    )
                  ) : (
                    isAllowedToArchiveMailingTemplates ? (
                      <CustomButton
                        color="error"
                        variant="outlined"
                        size="small"
                        sx={{ marginRight: '16px' }}
                        startIcon={<RedBoxIcon />}
                        onClick={handleArchiver}
                      >
                        В архив
                      </CustomButton>
                    ) : (
                      <CustomButton
                        color="error"
                        variant="outlined"
                        size="small"
                        sx={{ marginRight: '16px' }}
                        startIcon={<RedBoxIcon />}
                        disabled={true}
                        tooltip={DISABLED_CONTROL_TOOLTIP}
                      >
                        В архив
                      </CustomButton>
                    )
                  )
                }

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'left',
                    flexWrap: 'wrap',
                    gap: '50px',
                    marginTop: '24px'
                  }}>
                  <DoubleField
                    title="Период отправки"
                    description={ getTimeBound() }
                    data-testid="Period"
                  />
                  <DoubleField
                    title="Код типа коммуникации"
                    description={template?.channel_type_code}
                    data-testid="ChannelTypeCode"
                  />
                  <DoubleField
                    title="Приоритет отправки"
                    description={template?.priority}
                    data-testid="Priority"
                    tooltip={
                      !template || isNaN(template?.priority) ?
                        null :
                        PRIORITIES.find((priority) => priority.value === template.priority)?.label
                    }
                  />
                  <DoubleField
                    title="Тип рассылки"
                    description={getMailingType(template?.mailing_type)}
                    data-testid="MailingType"
                  />
                </Box>
              </Paper>

              <Paper
                sx={{
                  padding: '32px',
                  marginTop: '16px',
                  display: 'grid',
                  gridTemplateColumns: '3fr 1fr'
                }}>
                <Box>
                  {template?.mailing_type === 'email' && (
                    <DoubleField
                      display="flex"
                      mb={3}
                      sx={{ 'p:first-child': { marginRight: '24px' }, lineBreak: 'anywhere' }}
                      title="Заголовок сообщения"
                      description={template?.topic}
                    />
                  )}

                  <DoubleField
                    display="flex"
                    sx={{ 'p:first-child': { marginRight: '24px' }, lineBreak: 'auto' }}
                    title="Текст сообщения"
                    description={template?.text}
                  />
                </Box>
              </Paper>
            </>
          ) : (
            <NotFoundItems message="Вам запрещён доступ к просмотру списка событий" />
          )
        }
      </Box>

      <CustomModal
        opened={isSuccessModal}
        modalClose={() => setIsSuccessModal(false)}
        modalSubmit={handleArchiver}
        text={"Вы действительно хотите удалить данный шаблон?"}
        cancelBtnName="Отмена"
        submitBtnName="Да"
        data-testid="Warning Modal"
      />

      <CustomModal
        opened={isArchiveErrorModal}
        modalClose={() => {
          dispatch(clearArchiveTemplateErrors());
          setIsArchiveErrorModal(false);
        }}
        text="Данный шаблон используется!"
        subText={archiveError}
        withoutSubmit={true}
        data-testid="Warning Modal"
      />
  </>
  );
};
